/**
 * The scorch an aeroplane leaves where it went in — AD-014c's ground half.
 *
 * A sibling of `scene-shadows.ts`, deliberately: the two solve the same problem
 * — put a dark quad on a surface that bends — and this one reuses that module's
 * answer rather than inventing a second. `flight-scene.ts` keeps one field and
 * one constructor call; `scene-draw.ts` makes the frame-loop call. The `rules`
 * — how long a mark lasts and how big it is — are `crash-sites.ts`'s, in the
 * engine, and nothing here imports a tunable it invented.
 *
 * **Where the ground is.** `shadowPlaneAt` from `scene-shadows.ts`, which is the
 * *composed* `GroundQuery.groundHeightAt` the undercarriage stops on and the
 * shadow drapes to — `.GI` aerodrome facets over `ReliefGround.drapeAt`, never
 * the analytic relief field. That is the whole reason this module exists as a
 * neighbour rather than as a terrain feature: a mark that sampled a different
 * surface from the aeroplane that made it would float or sink, which is the
 * fault class `lessons.md` records four times. `shadowPlaneAt` also applies that
 * module's `SHADOW_LIFT_M` (0.2 m), which is the right clearance here for the
 * reason it is right there and for the reason it is 0.2 rather than the datum's
 * 0.08: this is a slope-fitted plane, not a draped mesh, and 20 cm covers the
 * bend between sampled facets at the relief lattice's cell sizes.
 *
 * **The engine decides where and when.** `EncounterState.crashSites` is sim
 * state, deterministic, and identical with the toggle on or off; this module
 * only draws it. A site's `y` is already the terrain height under the impact,
 * sampled in `encounter-tick.ts` from the encounter's own query — so the two
 * answers agree by construction and this module's own sample is used only for
 * the local *slope*.
 *
 * **Toggle.** ENHANCED EFFECTS, and nothing else, on `scene-shadows.ts`'s
 * argument verbatim in spirit: the original leaves nothing on the ground where
 * an aeroplane crashed (`crash-sites.ts` has the `pyro.c` evidence), so this is
 * a port extension and belongs under the toggle that already means "visual
 * phenomena the original did not draw". With it off, a sortie's pixels are what
 * they were before this feature existed.
 */

import * as THREE from "three/webgpu";
import { CRASH_TUNABLES, type CrashSite, type GroundQuery } from "@red-opus/engine";
import type { EffectsSettings } from "./effects-settings.ts";
import type { EncounterRunner } from "./sim-runner.ts";
import { shadowPlaneAt } from "./scene-shadows.ts";

/**
 * The scorch's colour. Near-black with a little brown in it — burnt earth and
 * charred fabric rather than the neutral grey the shadow uses, so a mark is not
 * mistaken for the shadow of something overhead.
 */
const MARK_COLOUR = 0x1a1108;

/**
 * Opacity a fresh mark is drawn at. Short of opaque because a scorch is soot on
 * a field rather than a hole in it, and because the ground texture reading
 * through is what stops it looking like a decal pasted on.
 */
const MARK_OPACITY = 0.72;

/**
 * How much longer than wide the streak is. Wreckage goes the way the aeroplane
 * was going, so the mark is an ellipse along the impact heading rather than a
 * disc — `CrashSite.headingRad` is carried for exactly this.
 */
const MARK_LENGTH_RATIO = 1.8;

/**
 * Seconds the mark takes to reach its full size, spreading from the impact
 * point. Not instant: an aeroplane arriving at forty metres a second slides,
 * and a scar that appeared full-grown on one frame reads as a decal being
 * switched on.
 */
const MARK_GROW_SECONDS = 2.5;

/** Module-level so the one constant rotation below is not a per-instance allocation. */
const X_AXIS = new THREE.Vector3(1, 0, 0);

/** One site's quad, kept between frames so a mark is built once and then only re-posed. */
interface MarkSlot {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.MeshBasicNodeMaterial;
}

export interface CrashMarks {
  /** Added to the scene once; every mark draws inside it. */
  readonly group: THREE.Group;
  update(sites: readonly CrashSite[], drawn: boolean): void;
  /** Marks currently on the ground — what a test counts. */
  placedCount(): number;
  dispose(): void;
}

/** 0..1 across `MARK_GROW_SECONDS`, then 1 for ever: the scar does not fade. */
export function markGrowth(site: CrashSite): number {
  return Math.min(1, site.ageSeconds / MARK_GROW_SECONDS);
}

/**
 * How wide the mark is at its full size, metres. One number for the whole fleet
 * scaled by nothing: `CRASH_TUNABLES.markSpreadM` is stated as a wingspan-ish
 * streak and the difference between a Camel's and a Gotha's is below what a
 * scorch on a field resolves to from any camera this scene has.
 */
export function markWidthM(site: CrashSite): number {
  return CRASH_TUNABLES.markSpreadM * markGrowth(site);
}

class CrashMarksImpl implements CrashMarks {
  readonly group = new THREE.Group();
  private readonly groundRef: { readonly current: GroundQuery };
  private readonly slots = new Map<string, MarkSlot>();
  /** One quad, shared by every mark — they differ only in their matrix and their material. */
  private readonly geometry = new THREE.PlaneGeometry(1, 1);
  private disposed = false;
  // Scratch, reused for every mark of every frame: placing one is a matrix
  // compose and these are where it is built. Nothing below allocates.
  private readonly normal = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly tilt = new THREE.Quaternion();
  private readonly spin = new THREE.Quaternion();
  /** Stands the XY plane geometry flat. Constant, so it is built once. */
  private readonly flat = new THREE.Quaternion().setFromAxisAngle(X_AXIS, -Math.PI / 2);
  private readonly orientation = new THREE.Quaternion();
  private readonly position = new THREE.Vector3();
  private readonly scale = new THREE.Vector3(1, 1, 1);

  constructor(groundRef: { readonly current: GroundQuery }) {
    this.groundRef = groundRef;
  }

  private slotFor(id: string): MarkSlot {
    const existing = this.slots.get(id);
    if (existing) return existing;
    const material = new THREE.MeshBasicNodeMaterial({ color: MARK_COLOUR, side: THREE.DoubleSide });
    material.transparent = true;
    material.opacity = MARK_OPACITY;
    // The same settlement `scene-shadows.ts` reaches: a mark must not write
    // depth, or the smoke and fire rising off it would be arbitrated against a
    // quad lying flat on the ground. `renderOrder` puts it ahead of every other
    // transparent thing for the same reason the shadow is at −1 — everything
    // else in this scene hangs in the air *above* the ground this is painted on.
    material.depthWrite = false;
    const mesh = new THREE.Mesh(this.geometry, material);
    mesh.renderOrder = -1;
    mesh.matrixAutoUpdate = false;
    const slot: MarkSlot = { mesh, material };
    this.slots.set(id, slot);
    this.group.add(mesh);
    return slot;
  }

  update(sites: readonly CrashSite[], drawn: boolean): void {
    if (this.disposed) return;
    this.group.visible = drawn;
    if (!drawn) return;
    const ground = this.groundRef.current;
    for (const site of sites) {
      const slot = this.slotFor(site.id);
      // The site's own `y` is the terrain height the engine sampled at the
      // impact; this call is for the local *slope*, and it re-reads the height
      // through the same query so a region that loaded after the crash lands the
      // mark on the surface that is actually drawn now.
      const plane = shadowPlaneAt(ground, site.x, site.z);
      this.normal.set(plane.normalX, plane.normalY, plane.normalZ).normalize();
      this.tilt.setFromUnitVectors(this.up, this.normal);
      // The geometry lies in XY, so it is stood flat, then turned to the impact
      // heading, then tilted onto the local slope — read right to left.
      this.spin.setFromAxisAngle(this.up, site.headingRad);
      this.orientation.copy(this.tilt).multiply(this.spin).multiply(this.flat);
      const width = markWidthM(site);
      this.position.set(plane.x, plane.y, plane.z);
      this.scale.set(width, width * MARK_LENGTH_RATIO, 1);
      slot.mesh.matrix.compose(this.position, this.orientation, this.scale);
      slot.mesh.matrixWorldNeedsUpdate = true;
      slot.mesh.visible = width > 0;
    }
  }

  placedCount(): number {
    return this.slots.size;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const slot of this.slots.values()) {
      this.group.remove(slot.mesh);
      slot.material.dispose();
    }
    this.slots.clear();
    this.geometry.dispose();
  }
}

export function createCrashMarks(
  scene: THREE.Scene,
  /**
   * The region's ground, through the same swappable ref the sim and the shadows
   * read. A ref rather than a value because `fly-region.ts` fills it after the
   * scene is built — and a mark placed on the flat default before then is on the
   * surface the aeroplane crashed onto too.
   */
  groundRef: { readonly current: GroundQuery },
): CrashMarks {
  const marks = new CrashMarksImpl(groundRef);
  scene.add(marks.group);
  return marks;
}

export interface CrashMarkHost {
  readonly runner: EncounterRunner;
  readonly effects: EffectsSettings;
  readonly crashMarks: CrashMarks;
}

/** Whether crash marks are drawn this frame — **ENHANCED EFFECTS, and nothing else** (see the module note). */
export function crashMarksDrawn(host: CrashMarkHost): boolean {
  return host.effects.enhancedEffects;
}

/** Module-level for the reason `crash-sources.ts` gives: this is a per-frame path. */
const NO_CRASH_SITES: readonly CrashSite[] = [];

export function updateCrashMarks(host: CrashMarkHost): void {
  host.crashMarks.update(host.runner.encounter().crashSites ?? NO_CRASH_SITES, crashMarksDrawn(host));
}
